import { useState } from "react";
import api, { formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Send, Bug, KeyRound, MessageSquareHeart } from "lucide-react";

const CATS = [
  { v: "bug", label: "Bug", icon: Bug },
  { v: "auth", label: "Login issue", icon: KeyRound },
  { v: "feedback", label: "Feedback", icon: MessageSquareHeart },
];

// tone: "dark" (Help drawer) | "light" (Settings)
const SupportWidget = ({ tone = "light" }) => {
  const { user } = useAuth();
  const [cat, setCat] = useState("feedback");
  const [msg, setMsg] = useState("");
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);

  const dark = tone === "dark";

  const submit = async () => {
    if (!msg.trim()) { toast.error("Please write a short message first."); return; }
    setSending(true);
    try {
      await api.post("/support/ticket", { category: cat, message: msg, email: user ? undefined : email });
      toast.success("Thank you — your message reached us. We read every one.");
      setMsg(""); setEmail("");
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  const chip = (active) =>
    dark
      ? `border ${active ? "bg-teal-200/20 border-teal-200/40 text-white" : "bg-white/5 border-white/10 text-white/55 hover:text-white/80"}`
      : `border ${active ? "bg-brand text-linen border-brand" : "bg-white border-linen-3 text-ink-2 hover:border-brand"}`;

  const field = dark
    ? "bg-white/5 border border-white/10 text-white placeholder-white/35 focus:border-teal-200/40"
    : "bg-linen border border-linen-3 text-ink placeholder-ink-mute focus:border-brand";

  return (
    <div data-testid="support-widget">
      <div className="flex flex-wrap gap-2 mb-3">
        {CATS.map((c) => (
          <button key={c.v} onClick={() => setCat(c.v)}
            className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs transition-colors ${chip(cat === c.v)}`}
            data-testid={`support-cat-${c.v}`}>
            <c.icon className="w-3.5 h-3.5" /> {c.label}
          </button>
        ))}
      </div>
      <textarea
        value={msg}
        onChange={(e) => setMsg(e.target.value)}
        rows={4}
        placeholder="Tell us what happened, what felt off, or what you'd love to see…"
        className={`w-full rounded-2xl px-4 py-3 text-sm outline-none resize-none transition-colors ${field}`}
        data-testid="support-message"
      />
      {!user && (
        <input
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Your email (optional, so we can reply)"
          className={`w-full rounded-2xl px-4 py-2.5 text-sm outline-none mt-2 transition-colors ${field}`}
          data-testid="support-email"
        />
      )}
      <button
        onClick={submit}
        disabled={sending}
        className={`mt-3 flex items-center justify-center gap-2 rounded-full px-5 py-2.5 text-sm transition-colors disabled:opacity-60 ${
          dark ? "bg-teal-200/15 border border-teal-200/30 text-white hover:bg-teal-200/25"
               : "bg-brand text-linen hover:bg-brand-hover"
        }`}
        data-testid="support-submit"
      >
        <Send className="w-4 h-4" /> {sending ? "Sending…" : "Send"}
      </button>
    </div>
  );
};

export default SupportWidget;
